import { Injectable } from '@angular/core';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { AutoUnsubscribe } from 'helpers';
import { Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { version } from 'config';
import { AuthenticatedUserService } from 'services/authenticatedUser';

import { environment } from '../../../environments/environment';

@AutoUnsubscribe
@Injectable({
    providedIn: 'root'
})
export class MonitoringService {
    public isLoggedInSubscription: Subscription;

    constructor(private authenticatedUserService: AuthenticatedUserService) {}

    public init() {
        if (MonitoringService.isDataDogEnabled()) {
            this.initDatadog();
            this.initUserContext();
        }
    }

    private static isDataDogEnabled() {
        return environment.DD_ENABLED;
    }

    private static isDataDogRumEnabled() {
        return environment.DD_RUM_ENABLED;
    }

    private initDatadog() {
        datadogLogs.init({
            clientToken: environment.DD_CLIENT_TOKEN,
            site: environment.DD_SITE as any,
            env: environment.DD_ENV,
            forwardErrorsToLogs: true,
            sessionSampleRate: 100,
            service: 'fc-app',
            version
        });

        if (MonitoringService.isDataDogRumEnabled()) {
            datadogRum.init({
                applicationId: environment.DD_APPLICATION_ID,
                clientToken: environment.DD_CLIENT_TOKEN,
                site: environment.DD_SITE,
                service: 'fc-app',
                env: environment.DD_ENV,
                version,
                sessionSampleRate: environment.DD_SESSION_SAMPLE_RATE,
                sessionReplaySampleRate: environment.DD_SESSION_REPLAY_SAMPLE_RATE,
                defaultPrivacyLevel: 'mask-user-input',
                trackUserInteractions: true,
                trackResources: true,
                trackLongTasks: true,
                traceSampleRate: environment.DD_TRACE_SAMPLE_RATE,
                traceContextInjection: 'sampled',
                enableExperimentalFeatures: ['feature_flags'],
                allowedTracingUrls: environment.DD_ALLOWED_TRACING_URLS,
                beforeSend: (event, context) => {
                    if (event.type === 'resource' && event.resource.url.endsWith('.svg')) {
                        // exclude svg resources to not bloat the datadog resources
                        return false;
                    }
                }
            });
        }
    }

    private initUserContext() {
        this.isLoggedInSubscription = combineLatest([
            this.authenticatedUserService.isLoggedIn.pipe(distinctUntilChanged()),
            this.authenticatedUserService.user
        ]).subscribe(([isLoggedIn, user]) => {
            datadogLogs.setGlobalContextProperty('user._id', isLoggedIn ? user?._id : null);
            if (MonitoringService.isDataDogRumEnabled() && isLoggedIn) {
                datadogRum.setGlobalContextProperty('user._id', user?._id);
                datadogRum.setUser({
                    id: user?._id
                });
            }
        });
    }
}
